import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LIVE_REFRESH_MS, saveDownload } from "@/lib/reporting";
import { Page, StatusBadge } from "@/components/lims";
import { ArrowLeft, Printer, DownloadSimple, CaretRight } from "@phosphor-icons/react";
import { canViewAudit } from "@/lib/roles";
import { NotFoundState } from "@/pages/NotFound";
import CertificatePanel from "@/components/CertificatePanel";
import SampleQrButton from "@/components/SampleQr";

const TABS = ["Overview", "Tests", "Custody", "Audit trail"];

export function hasReleasedReport(sample) {
  return sample?.status === "Released"
    || (sample?.status === "Disposed" && Boolean(sample?.qa_released_at));
}

function DefinitionList({ rows }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-8 gap-y-3 text-sm">
      {rows.filter(([, v]) => v !== undefined).map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400 pt-0.5">{label}</dt>
          <dd className="text-slate-900">{value ?? <span className="text-slate-300">—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

function LocationBreadcrumb({ location }) {
  if (!location?.box) return <span className="text-slate-300">Unassigned</span>;
  const parts = [
    location.freezer && { label: location.freezer.name },
    location.rack && { label: location.rack.name },
    { label: location.box.name },
    location.position && { label: location.position },
  ].filter(Boolean);
  return (
    <nav aria-label="Storage location" className="flex items-center gap-1 flex-wrap">
      {parts.map((p, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <CaretRight size={12} className="text-slate-300" />}
          <Link to="/storage" className="text-slate-700 hover:text-slate-900 hover:underline">{p.label}</Link>
        </span>
      ))}
    </nav>
  );
}

export default function SampleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [sample, setSample] = useState(null);
  const [custody, setCustody] = useState(null);
  const [audit, setAudit] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [error, setError] = useState(null);
  const showAudit = canViewAudit(user);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/samples/${id}?include_voided=true`);
      setSample(data);
      setError(null);
    } catch (err) {
      setError({
        status: err.response?.status,
        message: formatApiError(err.response?.data?.detail) || err.message,
      });
    }
  }, [id]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (tab === "Custody" && !custody) {
      api.get(`/samples/${id}/custody`).then(({ data }) => setCustody(data)).catch(() => setCustody({ events: [] }));
    }
    if (tab === "Audit trail" && !audit && showAudit) {
      api.get(`/audit?entity_id=${id}`).then(({ data }) => setAudit(data)).catch(() => setAudit([]));
    }
  }, [tab, id, custody, audit, showAudit]);

  const printLabel = async (size) => {
    try {
      const { data } = await api.get(`/samples/${id}/label?size=${size}`, { responseType: "text" });
      const w = window.open("", "_blank", "width=600,height=400");
      w.document.write(`<!doctype html><title>Label ${sample.sample_code}</title>
        <style>@media print { @page { size: ${size === "small" ? "50mm 25mm" : "100mm 50mm"}; margin: 0; } body { margin: 0; } }</style>
        ${data}`);
      w.document.close();
      w.focus();
      w.print();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadCustody = async () => {
    const { data } = await api.get(`/samples/${id}/custody?format=csv`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `custody_${sample.sample_code}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadReport = async () => {
    try {
      const { data } = await api.get(`/reports/samples/${id}.pdf`, {
        responseType: "blob",
      });
      saveDownload(data, `results-${sample.sample_code}.pdf`, "application/pdf");
      toast.success("Released report downloaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (error) {
    if (error.status === 404) {
      return (
        <Page>
          <NotFoundState
            title="Sample not found"
            message="This sample may have been removed from active records, or you may not have access to it."
            homeTo="/samples"
            homeLabel="View samples"
          />
        </Page>
      );
    }
    return (
      <Page>
        <div role="alert" className="rounded-md border border-status-critical/30 bg-status-critical/10 p-4 text-sm text-status-critical">
          <p>{error.message}</p>
          <button type="button" onClick={load} className="mt-3 font-medium underline underline-offset-2">
            Try again
          </button>
        </div>
      </Page>
    );
  }
  if (!sample) {
    return <Page><div className="h-32 bg-slate-100 rounded-lg animate-pulse" /></Page>;
  }

  const tabs = showAudit ? TABS : TABS.filter((t) => t !== "Audit trail");

  return (
    <Page>
      <div className="mb-6">
        <Link to="/samples" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-3">
          <ArrowLeft size={16} /> Samples
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-display font-semibold tracking-tight text-slate-900">{sample.sample_code}</h1>
              {sample.voided ? (
                <span title={sample.void_reason} className="text-xs font-semibold text-status-critical bg-status-critical/10 border border-status-critical/30 px-2.5 py-1 rounded-full">VOID</span>
              ) : (
                <StatusBadge status={sample.status} />
              )}
              <SampleQrButton sampleId={sample.id} sampleCode={sample.sample_code} />
            </div>
            <p className="text-slate-500 mt-1 text-sm">{sample.name} · {sample.client_name || "no client"}</p>
          </div>
          <div className="flex gap-2">
            {hasReleasedReport(sample) && (
              <button data-testid="download-released-report" onClick={downloadReport} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-white bg-slate-900 hover:bg-slate-700 transition-colors">
                <DownloadSimple size={16} /> Released report
              </button>
            )}
            <button data-testid="print-label-small" onClick={() => printLabel("small")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors">
              <Printer size={16} /> Label 50×25
            </button>
            <button data-testid="print-label-medium" onClick={() => printLabel("medium")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors">
              <Printer size={16} /> Label 100×50
            </button>
          </div>
        </div>
      </div>

      <div role="tablist" aria-label="Sample record" className="flex gap-1 border-b border-slate-200 mb-6">
        {tabs.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            data-testid={`tab-${t.toLowerCase().replace(" ", "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-4">Accession record</h2>
            <DefinitionList rows={[
              ["Client", sample.client_name],
              ["Sample type", sample.sample_type],
              ["Received", (sample.received_at || sample.received_date) ? (
                <time dateTime={sample.received_at || sample.received_date} data-timestamp>
                  {(sample.received_at || sample.received_date).slice(0, 10)}
                </time>
              ) : null],
              ["Collected", sample.collection_date || null],
              ["Description", sample.description || null],
              ["Project", sample.project || null],
              ["Source", sample.source || null],
              ["Volume", sample.volume ? `${sample.volume} ${sample.unit || ""}` : null],
              ["Concentration", sample.concentration || null],
              ["Registered by", sample.created_by],
              ["Registered at", sample.created_at ? new Date(sample.created_at).toLocaleString() : null],
              ["Location", <LocationBreadcrumb location={sample.location} />],
              ["Required tests", (sample.required_tests || []).map((t) => t.assay_name).join(", ") || null],
              ["Notes", sample.notes || null],
            ]} />
            {sample.voided && (
              <div className="mt-4 rounded-md border border-status-critical/30 bg-status-critical/10 p-3 text-sm text-status-critical">
                Voided by {sample.voided_by} on {new Date(sample.voided_date).toLocaleString()}: {sample.void_reason}
              </div>
            )}
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-4">Status timeline</h2>
            <ol data-testid="status-timeline" className="relative border-l border-slate-200 ml-2 space-y-4">
              {(sample.status_history || []).map((h, i) => (
                <li key={i} className="ml-4">
                  <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-slate-300" aria-hidden="true" />
                  <div className="text-sm font-medium text-slate-900">{h.action} <span className="text-slate-400 font-normal">→ {h.to}</span></div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {h.actor} · <time dateTime={h.timestamp} data-timestamp>{new Date(h.timestamp).toLocaleString()}</time>
                  </div>
                  {h.reason && <div className="text-xs text-slate-500 mt-0.5 italic">{h.reason}</div>}
                </li>
              ))}
            </ol>
          </div>
          {hasReleasedReport(sample) && (
            <CertificatePanel sampleId={id} user={user} />
          )}
        </div>
      )}

      {tab === "Tests" && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          {(sample.tests || []).length === 0 ? (
            <div className="p-6 text-sm text-slate-400">No tests on this sample</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Code</th>
                  <th className="px-4 py-3 font-semibold">Assay</th>
                  <th className="px-4 py-3 font-semibold">Assignee</th>
                  <th className="px-4 py-3 font-semibold">Result</th>
                  <th className="px-4 py-3 font-semibold">Flag</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {sample.tests.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{t.test_code}</td>
                    <td className="px-4 py-3 text-slate-900">{t.assay_name}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{t.assigned_to || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{t.result_value ? `${t.result_value} ${t.result_unit || ""}` : "—"}</td>
                    <td className="px-4 py-3">{t.result_flag ? <StatusBadge status={t.result_flag} /> : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "Custody" && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex justify-end mb-4">
            <button data-testid="detail-custody-csv" onClick={downloadCustody} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors">
              <DownloadSimple size={16} /> Export CSV
            </button>
          </div>
          {!custody ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : (
            <ol className="relative border-l border-slate-200 ml-2 space-y-4">
              {custody.events.map((e, i) => (
                <li key={i} className="ml-4">
                  <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-slate-300" aria-hidden="true" />
                  <div className="text-sm font-medium text-slate-900">{e.event}</div>
                  <div className="text-xs text-slate-500">{e.detail}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{e.actor || "—"} · {e.timestamp ? new Date(e.timestamp).toLocaleString() : "—"}</div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {tab === "Audit trail" && showAudit && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          {!audit ? (
            <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : audit.length === 0 ? (
            <div className="p-6 text-sm text-slate-400">No audit entries for this record</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">When</th>
                  <th className="px-4 py-3 font-semibold">Actor</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                  <th className="px-4 py-3 font-semibold">Detail</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100 last:border-0 align-top">
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{e.actor}<div className="text-slate-400">{e.actor_role}</div></td>
                    <td className="px-4 py-3 text-xs font-medium text-slate-700 uppercase">{e.action}</td>
                    <td className="px-4 py-3 text-slate-600">{e.summary}{e.reason && <div className="text-xs text-slate-400 italic mt-0.5">{e.reason}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Page>
  );
}
